/**
 * "Action needed" on the Overview: the problems the compliance agent told the
 * partner about, and the button that asks for a fresh check. Nothing renders
 * when there is nothing to fix.
 */
import { Button } from "@nt/ui/button";
import { FieldError } from "@nt/ui/field";
import { CircleAlert, ExternalLink } from "lucide-react";
import { useState } from "react";

import { Section } from "@/components/section";
import { ApiError } from "@/lib/api";
import { shortDate, shortDateTime } from "@/lib/format";
import { useWrite } from "@/lib/queries";
import type { ComplianceStatus } from "@/lib/types";

const RECOVERY =
  "Try again in a minute. If it keeps failing, write to support@nonresident.tax.";

export function CompliancePanel({
  status,
  readOnly = false,
}: {
  status: ComplianceStatus;
  /** A read-only (deactivated) account may not ask for a re-check: no button. */
  readOnly?: boolean;
}) {
  const recheck = useWrite<{ queued: true }, Record<string, never>>(
    "/api/partner/compliance/recheck",
    ["compliance"],
  );
  const [limited, setLimited] = useState(false);
  if (status.items.length === 0) return null;

  const queued = recheck.isSuccess;
  const coolingDown = !status.canRecheck || limited;
  const until = shortDateTime(status.recheckAvailableAt);
  const wait = until
    ? `You can ask again after ${until}.`
    : "You can ask again in a few hours.";
  const failed = recheck.isError && !limited;

  return (
    <Section
      title={
        <span className="flex items-center gap-2">
          <CircleAlert aria-hidden className="size-4 shrink-0 text-primary" />
          Action needed on your partner pages
        </span>
      }
      description={
        status.lastCheckedAt
          ? `Last checked ${shortDate(status.lastCheckedAt)}`
          : undefined
      }
    >
      <ul className="divide-y divide-border">
        {status.items.map((item) => (
          <li key={item.id} className="flex flex-col gap-1 py-3 first:pt-0">
            <p className="font-medium">{item.title}</p>
            {item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 break-all text-sm text-primary underline"
              >
                {item.url}
                <ExternalLink aria-hidden className="size-3.5 shrink-0" />
              </a>
            ) : null}
            <p className="text-sm">{item.fixText}</p>
            {item.deadlineAt ? (
              <p className="text-sm text-muted-foreground">
                Please fix this by {shortDate(item.deadlineAt)}.
              </p>
            ) : null}
          </li>
        ))}
      </ul>
      {readOnly ? null : (
        <div className="mt-4 flex flex-col items-start gap-2">
          <Button
            type="button"
            disabled={coolingDown || queued || recheck.isPending}
            onClick={() =>
              recheck.mutate(
                {},
                {
                  onError: (error) => {
                    if (
                      error instanceof ApiError &&
                      error.code === "RATE_LIMITED"
                    )
                      setLimited(true);
                  },
                },
              )
            }
          >
            I&apos;ve fixed it — check again
          </Button>
          {queued ? (
            <p role="status" className="text-sm">
              Thanks. We are checking your pages again. If everything is fixed,
              we will email you; anything still open stays listed here.
            </p>
          ) : coolingDown ? (
            <p className="text-sm text-muted-foreground">{wait}</p>
          ) : null}
          {failed ? <FieldError role="alert">{RECOVERY}</FieldError> : null}
        </div>
      )}
    </Section>
  );
}
